import { AUDIT_FIELDS } from "../data/fields";
import type { FieldTrend, MetricsTrend, ThroughputBucket, TrendBucket } from "../types";

export type Granularity = "day" | "week";
export type TrendWindow = "recent" | "all";

/** Which series a trend panel draws: the study-wide totals, or one scored
 *  field's slice of them. A field key rather than an enum because the scored
 *  set is the SERVER's, and a client that hardcodes it renders a stale list
 *  the moment the study changes what it scores. */
export type TrendField = "all" | string;

/** Every field the buckets carry, in `AUDIT_FIELDS` display order so the chip
 *  row reads the same way the case form does. A key the UI does not know goes
 *  last (sorted, so it is stable) rather than vanishing — the server's scored
 *  set is allowed to move ahead of this build. */
export function trendFields(t: MetricsTrend): string[] {
  const seen = new Set<string>();
  for (const b of [...t.daily, ...t.weekly]) for (const f of b.fields) seen.add(f.field);
  // Widened to string: the keys arriving here are the SERVER's scored set,
  // which is allowed to name a field this build's AuditFieldKey union has
  // never heard of. Narrowing would drop exactly the case the sort below
  // exists to render.
  const order: string[] = AUDIT_FIELDS.map((f) => f.key);
  return [
    ...order.filter((k) => seen.has(k)),
    ...[...seen].filter((k) => !order.includes(k)).sort(),
  ];
}

const RECENT: Record<Granularity, number> = { day: 30, week: 12 };

/** "Sep 1" or "Wk of Aug 31". Parsed as a local calendar date, never via
 *  `new Date("YYYY-MM-DD")`, which is UTC and would shift the label a day. */
export function bucketLabel(start: string, g: Granularity): string {
  const [y, m, d] = start.split("-").map(Number);
  const text = new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return g === "week" ? `Wk of ${text}` : text;
}

export function selectBuckets<T extends { start: string }>(series: T[], g: Granularity, w: TrendWindow): T[] {
  if (w === "all") return series;
  return series.slice(-RECENT[g]);
}

/** The five verdict counters, shared by a whole bucket and by one field's
 *  slice of it — so the rate helpers and `weeklyDelta` work on either without
 *  a second copy that could drift from this one. */
type Verdicts = Pick<TrendBucket, "judged" | "agentCorrect" | "coderCorrect" | "both" | "neither">;

export const agentRate = (b: Verdicts) => (b.agentCorrect + b.both) / b.judged;
export const coderRate = (b: Verdicts) => (b.coderCorrect + b.both) / b.judged;
export const agreementRate = (b: Verdicts) => (b.both + b.neither) / b.judged;

const measurable = (b: TrendBucket) => b.measured && b.judged > 0;

/** One field's slice of a bucket, or an all-zero one when the bucket carries
 *  none. A zero-filled gap bucket has no case behind it and sends no slices,
 *  and the callers below gate every rate on `bucket.measured` anyway — so a
 *  missing slice renders as the gap it is, never as a measured 0%. */
function sliceOf(b: TrendBucket, field: string): FieldTrend {
  return (
    b.fields.find((f) => f.field === field) ?? {
      field, judged: 0, agentCorrect: 0, coderCorrect: 0, both: 0, neither: 0,
      agree: 0, differ: 0, oneSided: 0, notComparable: 0,
    }
  );
}

/** One bucket as the four mutually-exclusive judgement outcomes — the same
 *  both / agent-only / coder-only / neither mix the by-field table draws as a
 *  100%-width bar, one bar per bucket instead of one per field. Counts AND
 *  shares travel together: the bar is drawn from the shares, the tooltip and
 *  the table view quote the counts, and neither has to re-derive the other.
 *
 *  Every share is null — never 0 — on a bucket below the blind-review floor or
 *  with nothing judged, so an unmeasured bucket renders as an empty slot rather
 *  than as a period where the agent got everything wrong. */
export interface OutcomePoint {
  start: string;
  label: string;
  measured: boolean;
  reviewed: number;
  judged: number;
  both: number;
  agent: number;
  coder: number;
  neither: number;
  bothShare: number | null;
  agentShare: number | null;
  coderShare: number | null;
  neitherShare: number | null;
}

/** `field` selects one scored field's slice; "all" is the study-wide totals.
 *  The floor is the BUCKET's either way — a field's counters are zeroed with
 *  the bucket's, so selecting a field can never surface a verdict the total
 *  does not already publish. */
export function outcomePoints(buckets: TrendBucket[], g: Granularity, field: TrendField = "all"): OutcomePoint[] {
  return buckets.map((b) => {
    const v: Verdicts = field === "all" ? b : sliceOf(b, field);
    const ok = b.measured && v.judged > 0;
    // The four counters partition `judged`, so the four shares sum to 1 on any
    // measured bucket — which is what makes the stack a full-height bar.
    const share = (n: number) => (ok ? n / v.judged : null);
    return {
      start: b.start,
      label: bucketLabel(b.start, g),
      measured: b.measured,
      // `reviewed` stays the bucket's: a review is of a CASE, and slicing it
      // per field would report the same reviews once for every field.
      reviewed: b.reviewed,
      judged: v.judged,
      both: v.both,
      agent: v.agentCorrect,
      coder: v.coderCorrect,
      neither: v.neither,
      bothShare: share(v.both),
      agentShare: share(v.agentCorrect),
      coderShare: share(v.coderCorrect),
      neitherShare: share(v.neither),
    };
  });
}

/** One bucket as the corpus mix the per-field corpus table draws: agree /
 *  differ / one side only / not comparable. Shares are over `total` (all four),
 *  not over `scored` — dropping the unscorable slice would draw a bucket where
 *  most cases could not be compared identically to one where most agreed.
 *  `scored` is agree + differ, the volume that actually carries the agreement
 *  signal, and is what the volume pane plots. */
export interface AgreementPoint {
  start: string;
  label: string;
  casesReceived: number;
  scored: number;
  total: number;
  agree: number;
  differ: number;
  oneSided: number;
  notComparable: number;
  agreeShare: number | null;
  differShare: number | null;
  oneSidedShare: number | null;
  notComparableShare: number | null;
}

/** The corpus counters for one bucket. A specific field reads that field's
 *  slice; "all" SUMS the bucket's per-field slices rather than using the
 *  bucket's own case-level `comparable`/`fullyAgreed`. Those two are counts of
 *  CASES ("did the whole case agree"), and mixing them into a bar whose other
 *  positions are per-field judgements would stack two different units. Summing
 *  the slices keeps every segment judgement-level and makes "All" exactly the
 *  per-field corpus table's total column. */
function corpusCounters(b: TrendBucket, field: TrendField) {
  if (field !== "all") {
    const f = sliceOf(b, field);
    return { agree: f.agree, differ: f.differ, oneSided: f.oneSided, notComparable: f.notComparable };
  }
  return b.fields.reduce(
    (acc, f) => ({
      agree: acc.agree + f.agree,
      differ: acc.differ + f.differ,
      oneSided: acc.oneSided + f.oneSided,
      notComparable: acc.notComparable + f.notComparable,
    }),
    { agree: 0, differ: 0, oneSided: 0, notComparable: 0 },
  );
}

export function agreementPoints(buckets: TrendBucket[], g: Granularity, field: TrendField = "all"): AgreementPoint[] {
  return buckets.map((b) => {
    const c = corpusCounters(b, field);
    const total = c.agree + c.differ + c.oneSided + c.notComparable;
    // Null, never 0, with nothing in the bucket: a period no case landed in is
    // a gap, not a period where nothing agreed.
    const share = (n: number) => (total > 0 ? n / total : null);
    return {
      start: b.start,
      label: bucketLabel(b.start, g),
      casesReceived: b.casesReceived,
      scored: c.agree + c.differ,
      total,
      ...c,
      agreeShare: share(c.agree),
      differShare: share(c.differ),
      oneSidedShare: share(c.oneSided),
      notComparableShare: share(c.notComparable),
    };
  });
}

export interface ThroughputPoint { start: string; label: string; reviews: number }
export function throughputPoints(buckets: ThroughputBucket[], g: Granularity): ThroughputPoint[] {
  return buckets.map((b) => ({ start: b.start, label: bucketLabel(b.start, g), reviews: b.reviewsSubmitted }));
}

export interface RateDelta { points: number; from: string; to: string }
/** The last two MEASURED weeks, in percentage points. Null with fewer than
 *  two — a delta against an unmeasured week would be a delta against 0%. */
export function weeklyDelta(weekly: TrendBucket[], rate: (b: Verdicts) => number): RateDelta | null {
  const m = weekly.filter(measurable);
  if (m.length < 2) return null;
  const prev = m[m.length - 2], last = m[m.length - 1];
  return { points: Math.round((rate(last) - rate(prev)) * 1000) / 10, from: prev.start, to: last.start };
}

export function hasTrend(t: MetricsTrend): boolean {
  return (
    t.daily.length > 0 ||
    t.weekly.length > 0 ||
    t.reviewsDaily.length > 0 ||
    t.reviewsWeekly.length > 0
  );
}
